import WebsiteLanguageSelect from '@formosa/shared/components/WebsiteLanguageSelect';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { useTravelTranslation } from '../app/TravelLocaleContext';
import { useAuth } from '../auth/AuthContext';
import SafeImage from '../components/SafeImage';
import useTravelImSession from '../features/messages/useTravelImSession';
import { travelMemberProductDisplayName } from '../features/member-identity/memberIdentityPresentation';
import { useNotificationCenterUnreadCount } from '../features/notification-center/useNotificationCenterUnreadCount';
import { INBOX_HEADER_BELL } from '../features/notification-center/inboxIaNav';
import {
  isPrimaryDestinationActive,
  primaryDestinationsForAuth,
} from './travelPrimaryNav';
import GlobalCreateFab, { shouldHideGlobalCreateFab } from './GlobalCreateFab';
import './travel-layout.css';
import '../features/group-trip-notice/group-trip-notice.css';
import '../features/social-notification/social-notification.css';

function selfCreatorPath(user) {
  const id = user?.memberId ?? user?.id;
  if (id == null || id === '') return null;
  const text = String(id);
  if (!/^[1-9]\d*$/.test(text)) return null;
  return `/social/creators/${text}`;
}

function NotificationBellBadge({ badge }) {
  if (!badge || badge.mode === 'hidden') return null;
  if (badge.mode === 'dot') {
    return (
      <span
        className="travel-header__notice-badge travel-header__notice-badge--dot"
        aria-hidden="true"
        data-testid="nav-notifications-badge-dot"
      />
    );
  }
  return (
    <span
      className="travel-header__notice-badge"
      aria-hidden="true"
      data-testid="nav-notifications-badge"
    >
      {badge.text}
    </span>
  );
}

function openGlobalCreate(event) {
  window.dispatchEvent(new CustomEvent('travel-global-create-open', {
    detail: { opener: event.currentTarget },
  }));
}

function destinationLabel(destination, t) {
  return destination.labelText || t(destination.labelKey);
}

function BottomNavIcon({ id }) {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true };
  if (id === 'explore') {
    return (
      <svg {...common}>
        <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M16 16l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (id === 'journeys' || id === 'public-journeys') {
    return (
      <svg {...common}>
        <path d="M5 7h14M5 12h14M5 17h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (id === 'create') {
    return (
      <svg {...common}>
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  if (id === 'notifications') {
    return (
      <svg {...common}>
        <path
          d="M6.5 17h11l-1.2-1.2V11a4.3 4.3 0 1 0-8.6 0v4.8L6.5 17zm5.5 3a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5.5 19c1.4-3 3.7-4.5 6.5-4.5s5.1 1.5 6.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function PrimaryCreateControl({
  destination,
  className,
  testIdSuffix = '',
  hideFab,
  authenticated,
  navigate,
  children,
}) {
  const { t } = useTravelTranslation();
  const testId = `${destination.testId}${testIdSuffix}`;
  return (
    <button
      type="button"
      className={className}
      data-testid={testId}
      aria-label={t('travel.create.trigger')}
      disabled={hideFab}
      aria-disabled={hideFab || undefined}
      onClick={(event) => {
        if (hideFab) return;
        if (!authenticated) {
          navigate('/login');
          return;
        }
        openGlobalCreate(event);
      }}
    >
      {children}
    </button>
  );
}

function PrimaryNavLink({ destination, t, pathname, className = '' }) {
  const label = destinationLabel(destination, t);
  const active = isPrimaryDestinationActive(pathname, destination);
  return (
    <Link
      to={destination.to}
      className={`${className}${active ? ' active' : ''}`.trim()}
      data-testid={destination.testId}
      aria-current={active ? 'page' : undefined}
    >
      {label}
    </Link>
  );
}

export default function TravelLayout() {
  const { status, user, logout } = useAuth();
  const { locale, t } = useTravelTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  useTravelImSession();
  /** 已確認線上 Session，或離線快取身分：可顯示會員 Header，不得閃 Guest 入口。 */
  const showMemberChrome = status === 'authenticated' || status === 'offline_cached';
  /** 僅在 bootstrap 完成且確認匿名時顯示 Guest 登入入口。 */
  const showGuestAuth = status === 'anonymous';
  const isAuthInitializing = status === 'unknown';
  const authenticated = status === 'authenticated';
  const {
    badge: centerBadge,
    ariaLabel: centerNotificationAriaLabel,
  } = useNotificationCenterUnreadCount();
  const destinations = primaryDestinationsForAuth(showMemberChrome);
  const avatar = resolveMemberMediaUrl(user?.avatarUrl || user?.avatar);
  const profileTo = selfCreatorPath(user);
  const memberProductName = travelMemberProductDisplayName(user, {
    genericFallback: t('travel.nav.member'),
  });
  const notificationAria = !centerBadge || centerBadge.mode === 'hidden'
    ? t('travel.nav.notificationCenter')
    : (centerNotificationAriaLabel || t('travel.nav.notificationCenter'));
  const hideFab = authenticated && shouldHideGlobalCreateFab(location.pathname);
  const journeyDiscovery = location.pathname === '/discover/journeys';
  const wideMain = /^\/public\/journeys\/|^\/discover\/journeys|^\/group-trips/.test(location.pathname);

  return (
    <div
      className={
        `travel-shell travel-shell--ia-v2${showMemberChrome ? ' travel-shell--authed' : ''}${isAuthInitializing ? ' travel-shell--auth-init' : ''}${hideFab ? ' travel-shell--fab-hidden' : ''}${wideMain ? ' travel-shell--wide-main' : ''}${journeyDiscovery ? ' travel-shell--journey-discovery' : ''}`
      }
    >
      <aside className="travel-sidebar" data-testid="travel-desktop-sidebar" aria-label={t('travel.nav.primary')}>
        <Link className="travel-sidebar__brand" to="/explore">{t('travel.nav.brand')}</Link>
        <nav className="travel-sidebar__nav" data-testid="travel-primary-nav">
          {destinations.map((destination) => {
            if (destination.kind === 'create') {
              return (
                <PrimaryCreateControl
                  key={destination.id}
                  destination={destination}
                  className="travel-sidebar__item travel-sidebar__item--create"
                  hideFab={hideFab}
                  authenticated={authenticated}
                  navigate={navigate}
                >
                  {destinationLabel(destination, t)}
                </PrimaryCreateControl>
              );
            }
            return (
              <PrimaryNavLink
                key={destination.id}
                destination={destination}
                t={t}
                pathname={location.pathname}
                className="travel-sidebar__item"
              />
            );
          })}
        </nav>
      </aside>

      <nav
        className="travel-compact-nav"
        aria-label={t('travel.nav.primary')}
        data-testid="travel-tablet-nav"
      >
        {destinations.map((destination) => {
          if (destination.kind === 'create') {
            return (
              <PrimaryCreateControl
                key={destination.id}
                destination={destination}
                className="travel-compact-nav__item travel-compact-nav__item--create"
                testIdSuffix="-tablet"
                hideFab={hideFab}
                authenticated={authenticated}
                navigate={navigate}
              >
                {destinationLabel(destination, t)}
              </PrimaryCreateControl>
            );
          }
          const active = isPrimaryDestinationActive(location.pathname, destination);
          const label = destinationLabel(destination, t);
          return (
            <Link
              key={destination.id}
              to={destination.to}
              className={`travel-compact-nav__item${active ? ' active' : ''}`}
              aria-current={active ? 'page' : undefined}
              title={label}
              data-testid={`${destination.testId}-tablet`}
            >
              {label.slice(0, 1)}
            </Link>
          );
        })}
      </nav>

      <header className="travel-header">
        <div className="travel-header__inner">
          <Link className="travel-brand" to="/explore">{t('travel.nav.brand')}</Link>

          <nav
            className="travel-header__primary travel-header__primary--desktop"
            aria-label={t('travel.nav.primary')}
            hidden
          />

          <div className="travel-header__actions">
            <WebsiteLanguageSelect locale={locale} t={t} />
            {showMemberChrome ? (
              <>
                <NavLink
                  className="travel-header__bell"
                  to={INBOX_HEADER_BELL.to}
                  aria-label={notificationAria}
                  title={notificationAria}
                  data-testid="nav-notifications"
                  data-inbox-shortcut={INBOX_HEADER_BELL.role}
                  data-inbox-owner={INBOX_HEADER_BELL.owner}
                >
                  <span className="travel-header__bell-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6.5 17h11l-1.2-1.2V11a4.3 4.3 0 1 0-8.6 0v4.8L6.5 17zm5.5 3a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <NotificationBellBadge badge={centerBadge} />
                </NavLink>
                {profileTo ? (
                  <Link
                    className="travel-header__avatar-link travel-header__avatar-link--desktop"
                    to="/me"
                    aria-label={t('travel.nav.me')}
                    data-testid="nav-self-profile"
                  >
                    <SafeImage className="travel-header__avatar" src={avatar} alt="" />
                  </Link>
                ) : (
                  <SafeImage className="travel-header__avatar travel-header__avatar-link--desktop" src={avatar} alt="" />
                )}
                <span className="travel-header__member travel-header__member--desktop">
                  {memberProductName}
                </span>
                <button
                  type="button"
                  className="travel-header__sign-out travel-header__sign-out--desktop"
                  onClick={logout}
                  data-testid="nav-sign-out"
                >
                  {t('travel.nav.signOut')}
                </button>
              </>
            ) : null}
            {showGuestAuth ? (
              <NavLink
                className="travel-header__auth-link travel-header__auth-link--combined"
                to="/login"
                state={{ from: location }}
                data-testid="nav-sign-in"
              >
                {t('travel.nav.signInRegister')}
              </NavLink>
            ) : null}
            {isAuthInitializing ? (
              <span
                className="travel-header__auth-pending"
                data-testid="nav-auth-pending"
                aria-busy="true"
                aria-label={t('travel.nav.checkingSession')}
              />
            ) : null}
          </div>
        </div>
      </header>

      <div className="travel-main"><Outlet /></div>

      {/* Sheet host：Desktop/Tablet 使用 primary ＋，Mobile 使用獨立建立按鈕 */}
      {showMemberChrome ? <GlobalCreateFab showTrigger={false} /> : null}

      <PrimaryCreateControl destination={destinations.find((item) => item.kind === 'create')}
        className="travel-mobile-create" testIdSuffix="-floating"
        hideFab={hideFab} authenticated={authenticated} navigate={navigate}>
        <BottomNavIcon id="create" />
      </PrimaryCreateControl>

      <nav
        className="travel-bottom-nav"
        aria-label={t('travel.nav.primaryMobile')}
        data-testid="travel-bottom-nav"
      >
        {destinations.filter((destination) => destination.kind !== 'create').map((destination) => {
          const active = isPrimaryDestinationActive(location.pathname, destination);
          const label = destinationLabel(destination, t);
          return (
            <Link
              key={destination.id}
              to={destination.to}
              className={`travel-bottom-nav__item${active ? ' is-active' : ''}`}
              aria-current={active ? 'page' : undefined}
              data-testid={`${destination.testId}-mobile`}
            >
              <BottomNavIcon id={destination.id} />
              <span>{label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
